/**
 * Strategy questionnaire session: answers, cursor, answerQuestion → Leave → leaveQuestionnaire.
 *
 * Intern cheat sheet:
 * 1. updateDraftAnswer — selection highlight only (does not move forward)
 * 2. answerQuestion — confirm answer; switch on result.type
 * 3. stageDone → animate (optional) then goNextStage
 * 4. allDone → animate (optional) then leaveQuestionnaire(result.leave)
 * 5. Always pass leave.answers into leaveQuestionnaire — never guess from React state
 * 6. Animations never carry answers
 * 7. Leave/exit/cancel resetCursorToStart (safety net — hub routes by status)
 * 8. Full-flow answers via useStrategySessionHydration → ApplyHydration
 *
 * Belongs here: draft/committed/versions, cursor, answerQuestion/leaveQuestionnaire (incl. hub SIP POST),
 *               edit/exit, riskProfile local state.
 * Does not belong here: Find-my-strategy (useStrategyResolve), review out-of-sync autosubmit (review page),
 *               Rive, URL flags, or FullFlow hydrate policy.
 */
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import type {
  StrategyQuestionnairePayload,
  StrategyQuestionnaireAnswersMap
} from '../../shared/types/questionnaire.types'
import { StrategyQuestionnaireStageId } from '../../shared/types/questionnaire.types'
import type { AllocationRiskProfile } from '../../types/allocation.types'
import { getVisibleQuestions } from '../../question-utils/visibleQuestions'
import { areAnswersEqual } from '../../question-utils/normalizeAnswersMap'
import { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import { useContentTransition } from '../../shared/transitions/useContentTransition'
import { getAnswersAfterAdd, getStrategyNextAfterAnswer, StrategyNextAfterAnswerTargetType } from './flowRules'
import {
  EXPERT_REVIEW_CAME_FROM,
  type ExpertReviewCameFrom,
  useExpertReviewNavigation
} from '../../navigation/expertReviewNavigation'
import { useSubmitStrategyAnswersMutation } from '../../api/useSubmitStrategyAnswersMutation'
import { pickAnswersForStage } from '../../question-utils/strategyAnswers'
import { useToast } from '../../../../../state-management/contexts/ToastContext'
import { useLoading } from '../../../../../state-management/contexts/LoadingContext'
import { EXPERT_REVIEW_SAVE_ERROR_TOAST } from '../../api/runExpertReviewSubmit'
import { getEditStagesInApiOrder } from '../../question-utils/stageEditScope'
import type { ExpertReviewStatus } from '../../providers/ExpertReviewStatusContext'
import { useStrategySessionHydration } from './useStrategySessionHydration'
import {
  initialStrategySessionState,
  STRATEGY_SESSION_MODE,
  StrategyConfirmOutcome,
  StrategySessionActionType,
  strategySessionReducer,
  type StrategySessionMode
} from './strategySessionReducer'

export { STRATEGY_SESSION_MODE }
export type { StrategySessionMode }
export { StrategyResolveResult } from './useStrategyResolve'

export interface GoToQuestionParams {
  nextStageIndex: number
  nextQuestionIndex: number
  direction: SlideDirection
}

/**
 * Where to send the user after answering is finished.
 * Built into the Leave plan at AllDone — leaveQuestionnaire does not re-guess.
 */
export enum StrategyWhereToGo {
  Review = 'review',
  Back = 'back',
  HubSip = 'hub_sip'
}

/**
 * Bundle handed from answerQuestion(allDone) → leaveQuestionnaire.
 *
 * Why answers are on the plan (not read from React state later):
 * Hub/edit leave often runs in the same tick as AnswerQuestion. Render state is still old.
 * leave.answers is the snapshot we just committed — hub SIP POSTs from this map.
 */
export type StrategyLeave = {
  whereToGo: StrategyWhereToGo
  answers: StrategyQuestionnaireAnswersMap
  /** committedAnswersVersion after this AllDone — same-tick Hub SIP POST must use this, not React state. */
  answersVersion: number
}

/**
 * Discriminator for StrategyAnswerResult — page-flow switches on this, never raw strings.
 */
export enum StrategyAnswerResultType {
  NextQuestion = 'next_question',
  StageDone = 'stage_done',
  AllDone = 'all_done',
  Ignored = 'ignored'
}

/**
 * Return value of answerQuestion — page-flow switches on `type` and does chrome/nav only.
 * Animations must never carry answers; only Leave does (for AllDone).
 */
export type StrategyAnswerResult =
  | { type: StrategyAnswerResultType.NextQuestion }
  | { type: StrategyAnswerResultType.StageDone; nextStageIndex: number }
  | { type: StrategyAnswerResultType.AllDone; leave: StrategyLeave }
  | { type: StrategyAnswerResultType.Ignored }

/**
 * Pick leave destination from product mode + route.
 * Hub SIP only when both HubSip mode and a stage id are present.
 */
function buildWhereToGo(sessionMode: StrategySessionMode, stageIdFromPathParam: string | undefined): StrategyWhereToGo {
  const stageIdBeingEdited = stageIdFromPathParam?.trim()
  if (sessionMode === STRATEGY_SESSION_MODE.HubSip && stageIdBeingEdited) {
    return StrategyWhereToGo.HubSip
  }
  if (stageIdBeingEdited) return StrategyWhereToGo.Back
  return StrategyWhereToGo.Review
}

/**
 * Strategy questionnaire session — one long-lived instance under the provider.
 *
 * @param stageIdFromPathParam — API stage id when editing one stage (`/strategy-questionnaire/:stageId`)
 * @param rawQuestionnaireData — full API payload from provider query
 * @param expertReviewStatus — /status loading + this questionnaire’s status
 */
export function useStrategyQuestionnaire(
  stageIdFromPathParam: string | undefined,
  rawQuestionnaireData: StrategyQuestionnairePayload | undefined,
  expertReviewStatus: ExpertReviewStatus
) {
  const {
    goToStrategyReview,
    goToStrategyStageEdit,
    goToStrategyQuestionnaire,
    goBackFromStrategyQuestionnaire
  } = useExpertReviewNavigation()
  const { showToast } = useToast()
  const { showLoading, hideLoading } = useLoading()

  const [session, dispatch] = useReducer(strategySessionReducer, initialStrategySessionState)
  const [riskProfile, setRiskProfile] = useState<AllocationRiskProfile | null>(null)

  useStrategySessionHydration({
    stageIdFromPathParam,
    rawQuestionnaireData,
    committedAnswers: session.committedAnswers,
    expertReviewStatus,
    dispatch
  })

  const markSubmittedAnswersVersion = useCallback((answersVersion: number) => {
    dispatch({
      type: StrategySessionActionType.MarkSubmittedAnswers,
      lastSubmittedAnswersVersion: answersVersion
    })
  }, [])

  const { submitAnswers, submitAnswersAndStatusRefresh } = useSubmitStrategyAnswersMutation({
    onSubmittedAnswersVersion: markSubmittedAnswersVersion
  })

  const { slideDirection, startTransition } = useContentTransition()

  /**
   * View model for the current route. When editing one stage (`/strategy-questionnaire/:stageId`),
   * `stages` is the edit set (edited stage + dependents) — stageIndex 0 is first of that set.
   */
  const questionnaireData = useMemo((): StrategyQuestionnairePayload | undefined => {
    if (!rawQuestionnaireData) return undefined
    if (!stageIdFromPathParam?.trim()) return rawQuestionnaireData
    const filtered = getEditStagesInApiOrder(stageIdFromPathParam, rawQuestionnaireData.stages ?? [])
    if (filtered.length === 0) return rawQuestionnaireData
    return { ...rawQuestionnaireData, stages: filtered }
  }, [rawQuestionnaireData, stageIdFromPathParam])

  const {
    draftAnswers,
    committedAnswers,
    stageIndex,
    questionIndex,
    sessionMode,
    hasReachedReviewOnce,
    committedAnswersVersion,
    lastSubmittedAnswersVersion
  } = session

  const isAnswersVersionOutOfSync = committedAnswersVersion !== lastSubmittedAnswersVersion
  const stage = questionnaireData?.stages[stageIndex]
  const visibleQuestions = useMemo(() => (stage ? getVisibleQuestions(stage, draftAnswers) : []), [stage, draftAnswers])
  const currentQuestion = visibleQuestions[questionIndex]
  const progressInStage = visibleQuestions.length > 0 ? (questionIndex + 1) / visibleQuestions.length : 0
  const stagesLength = questionnaireData?.stages?.length ?? 0
  const isLastStage = !questionnaireData || stageIndex >= Math.max(0, stagesLength - 1)

  // Stage edit route: reset cursor when :stageId changes (PrepareStageEdit also sets 0)
  useEffect(() => {
    if (!stageIdFromPathParam?.trim()) return
    dispatch({ type: StrategySessionActionType.ResetCursor })
  }, [stageIdFromPathParam])

  const goToQuestion = useCallback(
    ({ nextStageIndex, nextQuestionIndex, direction }: GoToQuestionParams) => {
      startTransition(direction)
      dispatch({
        type: StrategySessionActionType.MoveCursor,
        stageIndex: nextStageIndex,
        questionIndex: nextQuestionIndex
      })
    },
    [startTransition]
  )

  /** After stage animation (or immediately on edit): open the next stage at question 0. */
  const goNextStage = useCallback(
    (nextStageIndex: number) => {
      goToQuestion({
        nextStageIndex,
        nextQuestionIndex: 0,
        direction: SlideDirection.Forward
      })
    },
    [goToQuestion]
  )

  /** Remove one draft answer only — does not touch committed or move cursor. */
  function clearAnswer(questionId: string) {
    dispatch({ type: StrategySessionActionType.ClearQuestionAnswer, questionId })
  }

  /** Selection highlight only — updates draftAnswers; does not move forward or commit. */
  function updateDraftAnswer(questionId: string, value: string | number) {
    dispatch({
      type: StrategySessionActionType.UpdateDraftAnswer,
      questionId,
      value,
      stages: questionnaireData?.stages ?? []
    })
  }

  /**
   * User confirmed an answer on the current question.
   *
   * Flow:
   * 1. Peek nextAnswers + visibility (same helpers the reducer will use)
   * 2. Ask flowRules where to go
   * 3. Dispatch AnswerQuestion with matching outcome
   * 4. Return a simple result for page-flow (chrome / leave)
   *
   * On allDone the returned Leave carries answers — required for same-tick hub POST.
   */
  function answerQuestion(questionId: string, value: string | number): StrategyAnswerResult {
    if (!currentQuestion || !stage) return { type: StrategyAnswerResultType.Ignored }

    const stages = questionnaireData?.stages ?? []
    const nextAnswers = getAnswersAfterAdd(draftAnswers, questionId, value, stages)
    const visibleAfterAnswer = getVisibleQuestions(stage, nextAnswers)
    const advanceTarget = getStrategyNextAfterAnswer({
      stageIndex,
      questionIndex,
      currentQuestionId: currentQuestion.id,
      visibleQuestions,
      visibleAfterAnswer,
      isLastStage
    })

    if (advanceTarget.type === StrategyNextAfterAnswerTargetType.NextQuestion) {
      startTransition(SlideDirection.Forward)
      dispatch({
        type: StrategySessionActionType.AnswerQuestion,
        questionId,
        value,
        stages,
        outcome: {
          kind: StrategyConfirmOutcome.NextQuestion,
          stageIndex: advanceTarget.stageIndex,
          questionIndex: advanceTarget.questionIndex
        }
      })
      return { type: StrategyAnswerResultType.NextQuestion }
    }

    if (advanceTarget.type === StrategyNextAfterAnswerTargetType.NextStage) {
      dispatch({
        type: StrategySessionActionType.AnswerQuestion,
        questionId,
        value,
        stages,
        outcome: { kind: StrategyConfirmOutcome.StageDone }
      })
      return {
        type: StrategyAnswerResultType.StageDone,
        nextStageIndex: advanceTarget.nextStageIndex
      }
    }

    /**
     * All done. Clear risk when answers changed — except hub SIP
     * (product: SIP edit must not wipe an existing risk profile).
     */
    if (sessionMode !== STRATEGY_SESSION_MODE.HubSip) {
      if (!areAnswersEqual(nextAnswers, committedAnswers)) {
        setRiskProfile(null)
      }
    }

    dispatch({
      type: StrategySessionActionType.AnswerQuestion,
      questionId,
      value,
      stages,
      outcome: { kind: StrategyConfirmOutcome.AllDone }
    })

    const answersVersion = !areAnswersEqual(nextAnswers, committedAnswers)
      ? committedAnswersVersion + 1
      : committedAnswersVersion

    const leave: StrategyLeave = {
      whereToGo: buildWhereToGo(sessionMode, stageIdFromPathParam),
      answers: nextAnswers,
      answersVersion
    }
    return { type: StrategyAnswerResultType.AllDone, leave }
  }

  /** Reset draftAnswers to committedAnswers — drops unconfirmed highlights. */
  function discardDraftAnswers() {
    dispatch({ type: StrategySessionActionType.DiscardDraft })
  }

  /**
   * Park cursor at stage 0 / question 0.
   * AllDone intentionally keeps cursor to avoid a life-stage flash before leave —
   * callers must reset when leaving answering (review / exit / hub clear) so the
   * next full questionnaire open does not resume on the last question.
   */
  function resetCursorToStart() {
    dispatch({ type: StrategySessionActionType.ResetCursor })
  }

  /** Discard uncommitted draft, reset cursor, reset session mode — hub / menu leave. */
  function clearSession() {
    discardDraftAnswers()
    resetCursorToStart()
    dispatch({
      type: StrategySessionActionType.SetSessionMode,
      mode: STRATEGY_SESSION_MODE.Questionnaire
    })
  }

  /**
   * Execute a Leave plan from AllDone.
   *
   * HubSip → POST SIP slice from leave.answers, keep risk, pop
   * Back   → pop (review stage edit)
   * Review → replace to strategy review
   *
   * Always reset cursor after a successful leave — provider stays mounted.
   */
  async function leaveQuestionnaire(leave: StrategyLeave) {
    if (leave.whereToGo === StrategyWhereToGo.HubSip) {
      const stageIdBeingEdited = stageIdFromPathParam?.trim()
      const questionnaireId = rawQuestionnaireData?.questionnaireId ?? ''
      // Always slice from leave.answers — never from a stale render's committedAnswers
      const sipAnswers = stageIdBeingEdited
        ? pickAnswersForStage(leave.answers, stageIdBeingEdited, rawQuestionnaireData?.stages)
        : {}
      if (!questionnaireId || Object.keys(sipAnswers).length === 0) {
        showToast({ message: EXPERT_REVIEW_SAVE_ERROR_TOAST, duration: 2500 })
        return
      }

      showLoading()
      try {
        await submitAnswersAndStatusRefresh({
          questionnaireId,
          answers: sipAnswers,
          answersVersion: leave.answersVersion
        })
        dispatch({
          type: StrategySessionActionType.SetSessionMode,
          mode: STRATEGY_SESSION_MODE.Questionnaire
        })
        resetCursorToStart()
        goBackFromStrategyQuestionnaire()
      } catch {
        // Stay on edit route so the user can retry — keep cursor for retry
      } finally {
        hideLoading()
      }
      return
    }

    dispatch({
      type: StrategySessionActionType.SetSessionMode,
      mode: STRATEGY_SESSION_MODE.Questionnaire
    })
    // Navigate first while cursor still on finished stage — then reset for next open
    if (leave.whereToGo === StrategyWhereToGo.Back) {
      goBackFromStrategyQuestionnaire()
      resetCursorToStart()
      return
    }
    goToStrategyReview({
      cameFrom: EXPERT_REVIEW_CAME_FROM.STRATEGY_QUESTIONNAIRE,
      replace: true
    })
    resetCursorToStart()
  }

  /**
   * Clear edited stage (+ dependents) from committed, set sessionMode, open edit route.
   * Cursor lands at stageIndex 0 of the filtered edit set (see questionnaireData memo).
   */
  function prepareStageEdit(stageId: string, cameFrom: ExpertReviewCameFrom, mode: StrategySessionMode) {
    dispatch({
      type: StrategySessionActionType.PrepareStageEdit,
      stageId,
      stages: rawQuestionnaireData?.stages ?? [],
      sessionMode: mode
    })
    startTransition(SlideDirection.Forward)
    goToStrategyStageEdit({ stageId, cameFrom })
  }

  /** Hub Monthly SIP card — HubSip mode; finish POSTs SIP slice and keeps risk profile. */
  function openHubSipEdit() {
    prepareStageEdit(
      StrategyQuestionnaireStageId.SipAllocation,
      EXPERT_REVIEW_CAME_FROM.HUB,
      STRATEGY_SESSION_MODE.HubSip
    )
  }

  /** Discard uncommitted draft, then leave the questionnaire route. */
  function cancelAnswering() {
    discardDraftAnswers()
    dispatch({
      type: StrategySessionActionType.SetSessionMode,
      mode: STRATEGY_SESSION_MODE.Questionnaire
    })
    goBackFromStrategyQuestionnaire()
    resetCursorToStart()
  }

  /** Review (or caller) edit one stage — StageEdit mode, same prepareStageEdit path. */
  function beginEditOne(stageId: string, options?: { cameFrom?: ExpertReviewCameFrom }) {
    prepareStageEdit(
      stageId,
      options?.cameFrom ?? EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW,
      STRATEGY_SESSION_MODE.StageEdit
    )
  }

  /** Discard draft only and reopen full questionnaire from the start (committed stays until AllDone). */
  function beginEditAll() {
    dispatch({
      type: StrategySessionActionType.BeginEditAll,
      stages: rawQuestionnaireData?.stages ?? []
    })
    startTransition(SlideDirection.Forward)
    goToStrategyQuestionnaire({ cameFrom: EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW })
  }

  /** Sync risk from status/refetch / resolve compute — does not POST answers or change session maps. */
  function setRiskProfileFromStatus(nextRiskProfile: AllocationRiskProfile | null) {
    setRiskProfile(nextRiskProfile)
  }

  /** Hub resume — mark that full strategy review was opened this session. */
  const markReachedReview = useCallback(() => {
    dispatch({ type: StrategySessionActionType.MarkReachedReview })
  }, [])

  return {
    questionnaireData,
    draftAnswers,
    committedAnswers,
    committedAnswersVersion,
    lastSubmittedAnswersVersion,
    isAnswersVersionOutOfSync,
    submitAnswers,
    submitAnswersAndStatusRefresh,
    hasReachedReviewOnce,
    markReachedReview,
    stageIndex,
    questionIndex,
    updateDraftAnswer,
    answerQuestion,
    clearAnswer,
    leaveQuestionnaire,
    cancelAnswering,
    beginEditOne,
    beginEditAll,
    openHubSipEdit,
    clearSession,
    riskProfile,
    setRiskProfileFromStatus,
    sessionMode,
    stage,
    visibleQuestions,
    currentQuestion,
    progressInStage,
    isLastStage,
    slideDirection,
    goToQuestion,
    goNextStage,
    startTransition
  }
}
